import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

// Attach token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("aiq_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, token ? "token:YES" : "token:NO");
  return config;
});

// Handle 401 globally — but skip auth endpoints (login/register failures are expected)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err.config?.url || "";
    const isAuthEndpoint = url.includes("/api/auth/login") || url.includes("/api/auth/register");

    if (err.response?.status === 401 && !isAuthEndpoint) {
      console.warn("[API] 401 on protected route — clearing token", url);
      localStorage.removeItem("aiq_token");
      localStorage.removeItem("aiq_user");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────
export const registerUser = (data) => api.post("/api/auth/register", data);
export const loginUser = (data) => api.post("/api/auth/login", data);
export const googleAuth = (data) => api.post("/api/auth/google", data);
export const testRegister = (data) => api.post("/api/auth/test-register", data);
export const getMe = () => api.get("/api/auth/me");

// ── Quiz ──────────────────────────────────────────────
export const startQuiz = () => api.post("/api/quiz/start");
export const resumeQuiz = (sessionId) =>
  api.get(`/api/quiz/resume/${sessionId}`);
export const submitAnswer = (sessionId, data) =>
  api.post(`/api/quiz/answer?session_id=${sessionId}`, data);
export const sendHeartbeat = (sessionId, deltaSeconds) =>
  api.post(`/api/quiz/heartbeat?session_id=${sessionId}&delta_seconds=${deltaSeconds}`);
export const getResults = (sessionId) =>
  api.get(`/api/quiz/results/${sessionId}`);
export const getQuizHistory = () => api.get("/api/quiz/history");
export const checkCanRetake = () => api.get("/api/quiz/can-retake");
export const autoCompleteQuiz = (sessionId) =>
  api.post(`/api/quiz/auto-complete?session_id=${sessionId}`);
export const skipQuestion = (sessionId, excludeIds = []) =>
  api.post(`/api/quiz/skip-question?session_id=${sessionId}`, { exclude_question_ids: excludeIds });
export const getSessionCategoryBreakdown = (sessionId) =>
  api.get(`/api/quiz/category-breakdown/${sessionId}`);

// ── Admin ─────────────────────────────────────────────
export const getAdminResults = () => api.get("/api/admin/results");
export const getAdminUserResults = (userId) =>
  api.get(`/api/admin/results/${userId}`);
export const getAdminReport = (sessionId) =>
  api.get(`/api/admin/report/${sessionId}`);
export const getAdminViolations = (sessionId) =>
  api.get(`/api/admin/violations/${sessionId}`);
export const getSnapshotUrl = (logId) =>
  `${API_BASE}/api/admin/snapshot/${logId}`;
export const getSnapshotBlob = (logId) =>
  api.get(`/api/admin/snapshot/${logId}`, { responseType: "blob" });
export const adminReportViolation = (sessionId, data) =>
  api.post(`/api/admin/violation/${sessionId}`, data);
export const loadQuestionFixtures = () =>
  api.post("/api/admin/load-questions");
export const deleteAdminSession = (sessionId) =>
  api.delete(`/api/admin/session/${sessionId}`);
export const deleteUserHistory = (userId) =>
  api.delete(`/api/admin/user/${userId}/history`);

// ── Proctoring ───────────────────────────────────────
export const reportViolation = (data) =>
  api.post("/api/proctoring/violation", data);
export const uploadSnapshot = (data) =>
  api.post("/api/proctoring/snapshot", data);

// ── Dashboard (C-Level) ─────────────────────────────
export const getDashboardSummary = () => api.get("/api/dashboard/summary");
export const getDepartmentEmployees = (unit) =>
  api.get(`/api/dashboard/department/${encodeURIComponent(unit)}`);
export const getHierarchyLevelEmployees = (depth) =>
  api.get(`/api/dashboard/hierarchy-level/${depth}`);
export const getHierarchyView = (rootId, depth) => {
  const params = new URLSearchParams();
  if (rootId) params.set("root_employee_id", rootId);
  if (depth) params.set("depth", depth);
  return api.get(`/api/dashboard/hierarchy?${params}`);
};
export const getMyTeam = () => api.get("/api/dashboard/my-team");
export const getOrgTree = (rootId, depth) => {
  const params = new URLSearchParams();
  if (rootId) params.set("root_employee_id", rootId);
  if (depth) params.set("depth", depth);
  return api.get(`/api/dashboard/org-tree?${params}`);
};
export const getEmployeeHistory = (employeeId) =>
  api.get(`/api/dashboard/employee/${employeeId}/history`);
export const getLeaders = (minDepth = 1, maxDepth = 2) =>
  api.get(`/api/dashboard/leaders?min_depth=${minDepth}&max_depth=${maxDepth}`);
export const getLeaderTeam = (employeeId) =>
  api.get(`/api/dashboard/leader-team/${employeeId}`);
export const triggerEmployeeSync = () =>
  api.post("/api/dashboard/sync-employees");
export const getBeltDistribution = (unit) => {
  const params = unit ? `?business_unit=${encodeURIComponent(unit)}` : "";
  return api.get(`/api/dashboard/belt-distribution${params}`);
};
export const getOrgCategoryBreakdown = (unit) => {
  const params = unit ? `?business_unit=${encodeURIComponent(unit)}` : "";
  return api.get(`/api/dashboard/category-breakdown${params}`);
};
export const getEmployeeCategoryBreakdown = (employeeId, sessionId) => {
  const params = sessionId ? `?session_id=${sessionId}` : "";
  return api.get(`/api/dashboard/employee/${employeeId}/category-breakdown${params}`);
};
export const getCategorySessions = (category, businessUnit) => {
  const params = new URLSearchParams({ category });
  if (businessUnit) params.set("business_unit", businessUnit);
  return api.get(`/api/dashboard/category-sessions?${params}`);
};
export const getLeaderboard = (limit = 20, businessUnit) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (businessUnit) params.set("business_unit", businessUnit);
  return api.get(`/api/dashboard/leaderboard?${params}`);
};

// ── Admin: Retake Management ────────────────────────
export const getRetakeConfigs = () => api.get("/api/admin/retake-configs");
export const createRetakeConfig = (data) =>
  api.post("/api/admin/retake-configs", data);
export const grantRetake = (data) => api.post("/api/admin/grant-retake", data);
export const getRetakePermissions = () =>
  api.get("/api/admin/retake-permissions");
export const updateRetakePermission = (permissionId, data) =>
  api.put(`/api/admin/retake-permissions/${permissionId}`, data);

// ── Admin: Belt Calibration ─────────────────────────
export const getBeltConfigs = () => api.get("/api/admin/belt-configs");
export const createBeltConfig = (data) =>
  api.post("/api/admin/belt-configs", data);
export const updateBeltConfig = (configId, data) =>
  api.put(`/api/admin/belt-configs/${configId}`, data);
export const deleteBeltConfig = (configId) =>
  api.delete(`/api/admin/belt-configs/${configId}`);

// ── Admin: Employee Directory ───────────────────────
export const getAllEmployees = () => api.get("/api/admin/employees");

// ── LMS / Moodle integration ────────────────────────
export const getLmsHealth = () => api.get("/api/lms/health");
export const getLmsWhoAmI = () => api.get("/api/lms/whoami");
export const getLmsCombinedScores = (userId) => api.get(`/api/lms/scores/${userId}`);
export const getLmsCombinedScoresBulk = (userIds) =>
  api.get(`/api/lms/scores?user_ids=${userIds.join(",")}`);
// Diagnostic — admin only — dumps raw Moodle responses for one user.
export const getLmsRaw = (userId) => api.get(`/api/lms/raw/${userId}`);

// ── Assessments ─────────────────────────────────────
export const getMyAssessment = () => api.get("/api/assessments/me");
export const listAssessments = () => api.get("/api/assessments");
export const getAssessmentDetail = (id) => api.get(`/api/assessments/${id}`);
export const createAssessment = (data) => api.post("/api/assessments", data);
export const deleteAssessment = (id) => api.delete(`/api/assessments/${id}`);
export const uploadAssessmentQuestions = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/api/assessments/${id}/questions/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const uploadAssessmentUsers = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/api/assessments/${id}/users/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const revokeAssessmentUser = (assessmentId, userId) =>
  api.delete(`/api/assessments/${assessmentId}/users/${userId}`);

// ── User: Progress history ──────────────────────────
export const getMyProgress = () => api.get("/api/quiz/my-progress");

// ── Access Control (admin) ───────────────────────────
export const getMyDashboards = () => api.get("/api/access/me");
export const listAllDashboards = () => api.get("/api/access/dashboards");
export const listAccessUsers = () => api.get("/api/access/users");
export const listAccessGrants = () => api.get("/api/access/grants");
export const grantDashboardAccess = (data) =>
  api.post("/api/access/grant", data);
export const denyDashboardAccess = (data) =>
  api.post("/api/access/deny", data);
export const revokeDashboardAccess = (grantId) =>
  api.delete(`/api/access/grant/${grantId}`);
export const setUserRole = (userId, role) =>
  api.put(`/api/access/role/${userId}`, { role });

// ── Reports (PDF + Excel downloads) ─────────────────
// Each report endpoint streams a file; we request as blob so the browser can
// save it with the server-provided filename. Excel variants return the same
// data in .xlsx format with every Employee model field as a column.
const blobConfig = { responseType: "blob" };
export const downloadUserReport = (userId) =>
  api.get(`/api/reports/user/${userId}`, blobConfig);
export const downloadMyReport = () => api.get("/api/reports/me", blobConfig);
export const downloadTeamReport = (leaderEmployeeId) =>
  api.get(`/api/reports/team/${leaderEmployeeId}`, blobConfig);
export const downloadOrgReport = (businessUnit) => {
  const q = businessUnit
    ? `?business_unit=${encodeURIComponent(businessUnit)}`
    : "";
  return api.get(`/api/reports/organization${q}`, blobConfig);
};
export const downloadAssessmentStatusReport = ({
  leaderId,
  businessUnit,
  status = "all",
} = {}) => {
  const params = new URLSearchParams({ status });
  if (leaderId) params.set("leader_id", leaderId);
  if (businessUnit) params.set("business_unit", businessUnit);
  return api.get(`/api/reports/assessment-status?${params}`, blobConfig);
};

export const downloadUserReportExcel = (userId) =>
  api.get(`/api/reports/user/${userId}/excel`, blobConfig);
export const downloadMyReportExcel = () =>
  api.get("/api/reports/me/excel", blobConfig);
export const downloadTeamReportExcel = (leaderEmployeeId) =>
  api.get(`/api/reports/team/${leaderEmployeeId}/excel`, blobConfig);
export const downloadOrgReportExcel = (businessUnit) => {
  const q = businessUnit
    ? `?business_unit=${encodeURIComponent(businessUnit)}`
    : "";
  return api.get(`/api/reports/organization/excel${q}`, blobConfig);
};
export const downloadAssessmentStatusReportExcel = ({
  leaderId,
  businessUnit,
  status = "all",
} = {}) => {
  const params = new URLSearchParams({ status });
  if (leaderId) params.set("leader_id", leaderId);
  if (businessUnit) params.set("business_unit", businessUnit);
  return api.get(`/api/reports/assessment-status/excel?${params}`, blobConfig);
};

/**
 * Trigger a browser download from an axios blob response.
 * Picks filename from Content-Disposition header, falls back to a provided one.
 * Uses the response's Content-Type so PDF and Excel both save correctly.
 */
export const saveBlobAs = (response, fallbackName = "report") => {
  const cd = response.headers?.["content-disposition"] || "";
  const match = /filename="?([^"]+)"?/.exec(cd);
  const filename = match ? match[1] : fallbackName;
  const type = response.headers?.["content-type"] || "application/octet-stream";
  const blob = new Blob([response.data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default api;
